<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import { until, useResizeObserver, useScroll } from '@vueuse/core';
import { X } from 'lucide';
import { computed, nextTick, reactive, ref, useSlots, useTemplateRef, watch, watchEffect } from 'vue';
import { SphIcon } from '@/components/base/icon/index';
import { useWindowResize } from '@/composables/window-events';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogTitle,
} from '.';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = withDefaults(defineProps<Props>(), {
    class: '',
    overlayClass: '',
    title: '',
    persistent: false,
    noCloseButton: false,
    noBackdropDismiss: false,
    noEscapeDismiss: false,
    disableCloseButton: false,
    noHeader: false,
});

const emit = defineEmits<{
    'before-open': []
    open: []
    'before-close': [event: BeforeCloseEvent]
    close: []
    dismiss: [reason: string]
}>();

export interface Props {
    class?: HTMLAttributes['class']
    overlayClass?: HTMLAttributes['class']
    title?: string
    persistent?: boolean
    noCloseButton?: boolean
    noBackdropDismiss?: boolean
    noEscapeDismiss?: boolean
    disableCloseButton?: boolean
    noHeader?: boolean
}

const settings = computed(() => ({
    title: props.title,
    noCloseButton: props.noCloseButton || props.persistent,
    noBackdropDismiss: props.noBackdropDismiss || props.persistent,
    noEscapeDismiss: props.noEscapeDismiss || props.persistent,
    noHeader: props.noHeader,
}));

interface BeforeCloseEvent { prevented: boolean, prevent: () => void }

const state = defineModel('state', {
    type: Boolean,
    default: false,
});

const internalOpen = ref(state.value);
let _closingInternally = false;

// External state change (parent sets :state="false")
watch(state, (newVal) => {
    if (newVal) {
        internalOpen.value = true;
    } else if (!_closingInternally) {
        tryClose();
    }
});

function tryClose() {
    const event: BeforeCloseEvent = {
        prevented: false,
        prevent() {
            event.prevented = true;
        },
    };
    emit('before-close', event);
    if (!event.prevented) {
        _closingInternally = true;
        internalOpen.value = false;
        state.value = false;
        _closingInternally = false;
    } else {
        state.value = true; // restore model if parent had set it to false
    }
}

const dialog = reactive({
    open: () => {
        state.value = true;
    },
    close: tryClose,
});

const slots = useSlots();

const hasFooter = computed(() => !!slots.footer);

const slotInterface = { dialog };

function handleClickBackdrop() {
    if (settings.value.noBackdropDismiss) {
        return;
    }

    emit('dismiss', 'backdrop');
    tryClose();
}

function handleClickClose() {
    if (props.disableCloseButton) {
        return;
    }
    emit('dismiss', 'close-button');
    tryClose();
}

function handleKeyupEscape(event: KeyboardEvent) {
    if (event.key === 'Escape' && !settings.value.noEscapeDismiss) {
        emit('dismiss', 'escape');
        tryClose();
    }
}

const isBodyMounted = ref(false);

// Scroll Shadows
const bodyInner = useTemplateRef('bodyInner');
const bodyContent = useTemplateRef('bodyContent');
const shadowTop = useTemplateRef('shadowTop');
const shadowBottom = useTemplateRef('shadowBottom');
const { y, measure } = useScroll(bodyInner);

// Re-evaluate shadows whenever the slotted content resizes
// (async data, expanding sections, late-loading images/fonts).
useResizeObserver(bodyContent, () => {
    measure();
    updateShadows();
});

function updateShadows() {
    if (!shadowTop.value || !shadowBottom.value) {
        return;
    }

    shadowTop.value.classList.toggle('show', y.value > 0);
    shadowBottom.value.classList.toggle('show', y.value + bodyInner.value!.clientHeight < bodyInner.value!.scrollHeight - 1);
}

watch(internalOpen, (newVal) => {
    if (newVal) {
        nextTick(() => {
            measure();
        });
    }
}, { immediate: true });

watchEffect(() => {
    // update shadows whenever the scroll position changes (user scrolls, or content resizes)
    updateShadows();
});

useWindowResize(() => {
    if (internalOpen.value) {
        measure();
        updateShadows();
    }
});

const FOCUSABLE = 'input:not([type="hidden"]), select, textarea, button, a[href], [tabindex]:not([tabindex="-1"])';

function focusable(element: HTMLElement): HTMLElement | undefined {
    const candidates = [element, ...element.querySelectorAll<HTMLElement>(FOCUSABLE)];
    return candidates.find((candidate) => candidate.matches(FOCUSABLE)
        && !candidate.matches(':disabled')
        && (candidate.checkVisibility?.() ?? true));
}

// reka focuses the first tabbable element as the dialog mounts, before the body has rendered, which
// leaves it on the footer's first button. Once the body is there, a field marked `autofocus` takes
// focus instead — the field itself or, for a component, the first focusable element inside it.
async function onOpenAutoFocus(): Promise<void> {
    await until(isBodyMounted).toBe(true);
    await nextTick();
    const target = bodyContent.value?.querySelector<HTMLElement>('[autofocus]');
    if (target) {
        focusable(target)?.focus();
    }
}
</script>

<template>
    <Dialog
        :open="internalOpen"
        data-slot="dialog"
        :overlay-class="props.overlayClass"
    >
        <DialogContent
            :show-close-button="false"
            class="sph-dialog"
            :class="[{
                'has-header': !settings.noHeader,
                'has-footer': hasFooter,
                'has-close-button': !settings.noCloseButton,
            }, props.class]"
            :open="state"
            :aria-describedby="undefined"
            @enter="() => { isBodyMounted = true; emit('before-open') }"
            @after-enter="emit('open')"
            @after-leave="() => { emit('close'); isBodyMounted = false }"
            @keyup.esc="handleKeyupEscape"
            @open-auto-focus="onOpenAutoFocus"
        >
            <template #overlay>
                <DialogOverlay
                    class="sph-dialog-overlay"
                    :class="props.overlayClass"
                    @click="handleClickBackdrop"
                />
            </template>

            <DialogHeader
                v-if="!settings.noHeader"
                class="dialog-header"
            >
                <slot
                    name="header"
                    v-bind="slotInterface"
                >
                    <DialogTitle class="dialog-title">
                        <slot
                            name="title"
                            v-bind="slotInterface"
                        >
                            {{ settings.title }}
                        </slot>
                    </DialogTitle>
                </slot>
            </DialogHeader>

            <div class="dialog-body">
                <div
                    ref="shadowTop"
                    class="shadow-top"
                ></div>
                <div
                    ref="shadowBottom"
                    class="shadow-bottom"
                ></div>

                <div
                    ref="bodyInner"
                    class="inner"
                >
                    <div ref="bodyContent">
                        <slot
                            v-if="isBodyMounted"
                            v-bind="slotInterface"
                        ></slot>
                    </div>
                </div>
            </div>

            <DialogFooter
                v-if="hasFooter"
                class="dialog-footer"
            >
                <slot
                    name="footer"
                    v-bind="slotInterface"
                ></slot>
            </DialogFooter>

            <DialogClose
                v-if="!settings.noCloseButton"
                data-slot="dialog-close"
                class="dialog-close"
                :disabled="props.disableCloseButton"
                @click="handleClickClose"
                @keyup.enter="handleClickClose"
            >
                <SphIcon
                    :icon="X"
                    size="xl"
                />
                <span class="sr-only">Close</span>
            </DialogClose>
        </DialogContent>
    </Dialog>
</template>

<style>
@reference '@/assets/styles/index.css';

.sph-dialog-overlay {
    background-color: rgb(170 170 170 / 0.75);
    backdrop-filter: blur(2px);
}

/* An overlay that follows an open dialog belongs to one stacked on top of it: the page is already
   dimmed, so it only sets the dialog below back instead of darkening and blurring everything again. */
[data-slot='dialog-content'] ~ .sph-dialog-overlay {
    background-color: rgb(170 170 170 / 0.35);
    backdrop-filter: none;
}

html[data-kgr-theme='dark'] {
    .sph-dialog-overlay {
        background-color: rgb(0 0 0 / 0.6);
    }

    [data-slot='dialog-content'] ~ .sph-dialog-overlay {
        background-color: rgb(0 0 0 / 0.3);
    }
}

.sph-dialog {
    --close-button-color: var(--color-primary);
    --padding-x: calc(var(--spacing) * 6);
    --padding-y: calc(var(--spacing) * 4);
    --padding-body-x: calc(var(--spacing) * 4);
    --padding-body-y: calc(var(--spacing) * 2);
    --screen-padding: calc(var(--spacing) * 6);

    display: flex;
    flex-direction: column;
    gap: 0;
    width: 100vw;
    min-width: calc(var(--container-xs) - var(--screen-padding));
    max-width: calc(100vw - var(--screen-padding));
    max-height: calc(100vh - var(--screen-padding));
    padding: calc(var(--spacing) * 2) 0 0;
    background-color: var(--color-background);
    border: none;
    border-radius: var(--radius-lg);

    @apply sm:w-fit;

    @media (min-width: theme(--breakpoint-sm)) {
        --padding-body-x: calc(var(--spacing) * 6);
        --padding-body-y: calc(var(--spacing) * 4);
    }

    &:focus-visible {
        outline: none;
    }

    &.has-close-button {
        .dialog-header {
            padding-right: calc(var(--spacing) * 6 + var(--padding-x));
        }
    }

    &.has-footer {
        .dialog-body {
            padding-bottom: 0;
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
        }
    }

    &.has-header {
        --close-button-color: #fff;
        padding-top: 0;
        background-color: transparent;

        .dialog-body {
            padding-top: 1px;
            border-top-left-radius: 0;
            border-top-right-radius: 0;
        }
    }

    /* background layer to cover the gap between header, body and footer */
    &::before {
        content: '';
        position: absolute;
        top: var(--radius-lg);
        left: 1px;
        width: calc(100% - 2px);
        height: calc(100% - var(--radius-lg) * 2);
        background-color: var(--color-background);
        z-index: -1;
        border-radius: var(--radius-lg);
    }

    .dialog-header {
        background-color: var(--color-primary);
        color: #fff;
        padding: var(--padding-y) var(--padding-x);
        border-top-left-radius: var(--radius-lg);
        border-top-right-radius: var(--radius-lg);
    }

    .dialog-title {
        font-size: var(--text-xl);
        font-weight: var(--font-weight-semibold);
        line-height: var(--leading-snug);
        white-space: normal;

        .sph-badge {
            line-height: var(--leading-snug);
        }
    }

    .dialog-body {
        position: relative;
        display: flex;
        flex-flow: column;
        background-color: var(--color-background);
        flex: 1 1 auto;
        overflow-y: hidden;
        border-radius: var(--radius-lg);
        padding: 0 0 var(--spacing);

        > .inner {
            position: relative;
            flex: 1 1 auto;
            background-color: var(--color-background);
            padding: var(--padding-body-y) var(--padding-body-x);
            min-width: 100%;
            max-width: 100%;
            width: fit-content;
            height: 100%;
            overflow: hidden;
            overflow-y: auto;
            z-index: 1;
        }

        > .shadow-top,
        > .shadow-bottom {
            position: absolute;
            left: 0;
            z-index: 100;
            width: 100%;
            height: 1.5rem;
            z-index: 10;
            pointer-events: none;
            user-select: none;
            opacity: 0;
            transition:
                opacity 0.25s,
                transform 0.25s var(--ease-out-cubic);

            &.show {
                opacity: 1;
                transform: translate(0, 0);
            }
        }

        > .shadow-top {
            top: 0;
            background: radial-gradient(farthest-side at 50% 0%, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 100%) center
                center;
            background-size: 150% 100%;
            transform: translate(0, -50%);
        }

        > .shadow-bottom {
            bottom: 0;
            background: radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 100%) center
                center;
            background-size: 150% 100%;
            transform: translate(0, 50%);
        }
    }

    .dialog-footer {
        flex-flow: row-reverse wrap;
        justify-content: flex-start;
        gap: calc(var(--spacing) * 3);
        background-color: var(--color-background);
        padding: var(--padding-y) var(--padding-x);
        border-top: 1px solid var(--color-border-light);
        border-bottom-left-radius: var(--radius-lg);
        border-bottom-right-radius: var(--radius-lg);
    }

    .dialog-close {
        position: absolute;
        top: calc(var(--padding-y) - 0.35rem);
        right: calc(var(--padding-x) - 0.4rem);
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--close-button-color);
        background-color: transparent;
        border: none;
        border-radius: var(--radius-xs);
        transition: color 0.25s var(--ease-out-cubic);

        &:hover {
            color: var(--color-slate-400);
        }

        &:focus-visible {
            box-shadow: 0 0 0 2px var(--color-accent);
            outline: none;
        }

        svg {
            pointer-events: none;
        }
    }
}
</style>
